import React from 'react';
import { styled, tooltipClasses } from '@mui/material';
import { createPortal } from 'react-dom';
import { DesignTokens } from '../specify';
import { GivelifyTooltip } from '../tooltip';
import { GivelifyTooltipProps } from '../tooltip/types';

export type GivelifyTooltipModalProps = Partial<GivelifyTooltipProps> & {
    open: boolean;
    children: React.ReactElement;
    size?: 'small' | 'default';
    pulsingBagde?: boolean;
    closeOnClickAway?: boolean;
    backdropPortalRef?: HTMLElement;
};

const GivelifyTooltipModalBackdrop = styled('div')({
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: DesignTokens.color.overlayModal,
    zIndex: 1100,
});

const StyledTooltip = styled(GivelifyTooltip, {
    shouldForwardProp: (propName) =>
        propName !== 'size' && propName !== 'pulsingBadge',
})<{ size: 'small' | 'default'; pulsingBadge?: boolean }>(
    ({ size, theme, pulsingBadge }) => ({
        '& .MuiTooltip-tooltip': {
            borderRadius: '8px',
            padding: size === 'small' ? '24px' : '32px',
            minWidth: 'fit-content',
            [theme.breakpoints.down('mobile')]: {
                padding: size === 'small' ? '16px' : '24px',
                maxWidth: '92%',
                minWidth: 'auto',
                margin: 'auto',
            },

            ...(pulsingBadge && {
                marginTop: '32px !important',
            }),
        },

        ...(pulsingBadge && {
            '@keyframes pulse': {
                '0%': {
                    transform: 'translateX(-50%) scale(1)',
                    boxShadow: `0 0 0 0 rgba(113, 90, 255, 0.1)`,
                },
                '30%': {
                    transform: 'translateX(-50%) scale(1.1)',
                    boxShadow: `0 0 0 0px rgba(113, 90, 255, 0.2)`,
                },
                '35%': {
                    transform: 'translateX(-50%) scale(1)',
                },
                '90%': {
                    boxShadow: `0 0 0 12px rgba(113, 90, 255, 0.2)`,
                },
                '100%': {
                    boxShadow: `0 0 0 1px rgba(113, 90, 255, 0.1)`,
                },
            },
            [`& .${tooltipClasses.arrow}`]: {
                position: 'relative',
                overflow: 'unset',
                '&::after': {
                    content: '""',
                    position: 'absolute',
                    top: '-1rem',
                    left: '50%',
                    transform: 'translateX(-50%)',
                    fontSize: '1rem',
                    width: '14px',
                    height: '14px',
                    background: DesignTokens.color.globalGuidingViolet400,
                    borderRadius: '50%',
                    animation: 'pulse 1s infinite',
                },
            },
        }),
    }),
);

export const GivelifyTooltipModal: React.FCC<GivelifyTooltipModalProps> = ({
    open,
    children,
    placement,
    size = 'default',
    title,
    pulsingBagde,
    closeOnClickAway,
    backdropPortalRef,
    ...props
}) => {
    return children ? (
        <span>
            {open ? (
                backdropPortalRef ? (
                    createPortal(
                        <GivelifyTooltipModalBackdrop
                            onClick={
                                closeOnClickAway ? props.onClose : undefined
                            }
                        />,
                        backdropPortalRef,
                    )
                ) : (
                    <GivelifyTooltipModalBackdrop
                        onClick={closeOnClickAway ? props.onClose : undefined}
                    />
                )
            ) : null}

            <StyledTooltip
                {...props}
                arrow
                disableFocusListener
                disableHoverListener
                disableTouchListener
                open={open}
                placement={placement}
                pulsingBadge={pulsingBagde}
                size={size}
                title={title}
            >
                {children}
            </StyledTooltip>
        </span>
    ) : null;
};
